// src/pages/employer/EmployerJobAds.jsx
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { orgAPI, jobAdAPI } from '../../services/api';
import { useConfirm, EditBtn, AddBtn, DelBtn, usePersistedState } from '../../components/shared/UI';
import { X, FileText } from 'lucide-react';

// ── Shared styles ─────────────────────────────────────────

const inputStyle = {
  width: '100%', padding: '7px 10px', borderRadius: 6,
  border: '1px solid var(--border)', fontSize: 13,
  background: 'white', color: 'var(--text)', outline: 'none',
  boxSizing: 'border-box', fontFamily: 'inherit',
};
const labelStyle = {
  display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4,
};
const fieldWrap = { marginBottom: 14 };
const editBtnStyle = {
  background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
  color: 'white', borderRadius: 4, padding: '2px 8px', cursor: 'pointer',
  fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3,
  fontFamily: 'inherit',
};

const STATUS = {
  DRAFT:    { label: 'Draft',    color: '#777',    bg: '#f5f5f5' },
  ACTIVE:   { label: 'Active',   color: '#28A745', bg: '#e8f5e9' },
  PAUSED:   { label: 'Paused',   color: '#E67E22', bg: '#FEF9E7' },
  CLOSED:   { label: 'Closed',   color: '#CC0000', bg: '#fde8e8' },
  ARCHIVED: { label: 'Archived', color: '#555',    bg: '#eee' },
};

// ── SectionModal ──────────────────────────────────────────

const SectionModal = ({ title, onSave, onCancel, saving, children }) =>
  ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 1000 }}>
      <div className="modal-box" style={{ maxWidth: 560, width: '92vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>{title}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} style={{ padding: '4px 6px' }}>
            <X size={15} />
          </button>
        </div>
        <div className="modal-body" style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>
          {children}
        </div>
        <div className="modal-footer" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

// ── JobAdModal ────────────────────────────────────────────

const JobAdModal = ({ ad, contacts, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title:       ad?.title || '',
    description: ad?.description || '',
    closingDate: ad?.closingDate ? ad.closingDate.slice(0, 10) : '',
    contactId:   ad?.contact?.id || '',
  });
  const [saving, setSaving] = useState(false);
  const setF = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await onSave({
        title:       form.title.trim(),
        description: form.description || null,
        closingDate: form.closingDate || null,
        contactId:   form.contactId || null,
      });
    } finally { setSaving(false); }
  };

  return (
    <SectionModal title={ad ? 'Edit Job Ad' : 'Create Job Ad'} onSave={handleSave} onCancel={onCancel} saving={saving}>
      <div style={fieldWrap}>
        <label style={labelStyle}>Title *</label>
        <input type="text" value={form.title} onChange={setF('title')} style={inputStyle}
          placeholder="e.g. Now Hiring — Customer Service" autoFocus />
      </div>
      <div style={fieldWrap}>
        <label style={labelStyle}>Description</label>
        <textarea value={form.description} onChange={setF('description')}
          style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
          placeholder="Overview of the opportunity, team, culture…" />
      </div>
      <div className="form-row">
        <div style={fieldWrap}>
          <label style={labelStyle}>Closing Date</label>
          <input type="date" value={form.closingDate} onChange={setF('closingDate')} style={inputStyle} />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>Contact Person</label>
          <select value={form.contactId} onChange={setF('contactId')} style={inputStyle}>
            <option value="">— None —</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.position ? ` — ${c.position}` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>
    </SectionModal>
  );
};

// ── JobAdCard ─────────────────────────────────────────────

const JobAdCard = ({ ad, contacts, isAdmin, onUpdated, onDeleted, onEdit, onSelect }) => {
  const navigate = useNavigate();
  const { activeOrg, activeProfile } = useAuth();
  const { confirm, dialog } = useConfirm();
  const [saving, setSaving] = useState(false);

  const status = STATUS[ad.status] || STATUS.DRAFT;

  const handlePublish = async (e) => {
    e.stopPropagation();
    setSaving(true);
    try {
      const { data } = await jobAdAPI.publishJobAd(activeOrg.id, activeProfile.id, ad.id);
      onUpdated(data.data.ad);
    } finally { setSaving(false); }
  };

  const handleClose = async (e) => {
    e.stopPropagation();
    const ok = await confirm('Close Job Ad', 'Close this ad? It will no longer appear to candidates.');
    if (!ok) return;
    setSaving(true);
    try {
      const { data } = await jobAdAPI.closeJobAd(activeOrg.id, activeProfile.id, ad.id);
      onUpdated(data.data.ad);
    } finally { setSaving(false); }
  };

  const handleDeleteAd = async (e) => {
    e.stopPropagation();
    const ok = await confirm('Delete Job Ad', `Delete "${ad.title}"? This cannot be undone.`);
    if (!ok) return;
    await jobAdAPI.deleteJobAd(activeOrg.id, activeProfile.id, ad.id);
    onDeleted(ad.id);
  };

  return (
    <>
      {dialog}
      <div
        className="section-box fade-in"
        style={{ marginBottom: 12, cursor: 'pointer' }}
        onClick={() => { onSelect(ad.id); navigate(`/employer/job-ads/${ad.id}`); }}
      >
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, flex: 1 }}>{ad.title}</span>

          <span style={{
            fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10, flexShrink: 0,
            background: status.bg, color: status.color, border: `1px solid ${status.color}40`,
          }}>{status.label}</span>

          <span style={{ fontSize: 11, opacity: 0.75, fontWeight: 400, flexShrink: 0 }}>
            {ad.positions.length} pos.
          </span>

          {ad.closingDate && (
            <span style={{ fontSize: 11, opacity: 0.7, fontWeight: 400, flexShrink: 0 }}>
              Closes {new Date(ad.closingDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
            </span>
          )}

          {isAdmin && (
            <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexShrink: 0 }}
              onClick={(e) => e.stopPropagation()}>
              {(ad.status === 'DRAFT' || ad.status === 'PAUSED') && (
                <button style={{ ...editBtnStyle, background: 'rgba(40,167,69,0.25)', border: '1px solid rgba(40,167,69,0.5)' }}
                  onClick={handlePublish} disabled={saving}>Publish</button>
              )}
              {ad.status === 'ACTIVE' && (
                <button style={editBtnStyle} onClick={handleClose} disabled={saving}>Close</button>
              )}
              <EditBtn onClick={(e) => { e.stopPropagation(); onEdit(ad); }} title="Edit" />
              <DelBtn onClick={handleDeleteAd} title="Delete" />
            </div>
          )}
        </div>

        {ad.description && (
          <div style={{ padding: '10px 16px', fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.5 }}>
            {ad.description.length > 120 ? `${ad.description.slice(0, 120)}…` : ad.description}
          </div>
        )}
      </div>
    </>
  );
};

// ── Main page ─────────────────────────────────────────────

export const EmployerJobAds = () => {
  const { activeOrg, activeProfile, isAdmin } = useAuth();
  const [lastAdId, setLastAdId] = usePersistedState('tikhet:jobads:lastAdId', null);
  const [ads, setAds]           = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [adModal, setAdModal]   = useState(null); // null | { mode: 'create'|'edit', ad? }

  // Redirect to last-viewed ad on mount (cleared by "Job Ads" link in mockup toolbar)
  if (lastAdId) return <Navigate to={`/employer/job-ads/${lastAdId}`} replace />;

  const load = useCallback(async () => {
    if (!activeOrg || !activeProfile) { setLoading(false); return; }
    setLoading(true);
    try {
      const [adsRes, ctRes] = await Promise.all([
        jobAdAPI.getJobAds(activeOrg.id, activeProfile.id),
        orgAPI.getProfileContacts(activeOrg.id, activeProfile.id),
      ]);
      setAds(adsRes.data.data.ads || []);
      setContacts(ctRes.data.data.contacts || []);
    } finally { setLoading(false); }
  }, [activeOrg, activeProfile]);

  useEffect(() => { load(); }, [load]);

  const handleSaveAd = async (formData) => {
    if (adModal?.mode === 'edit') {
      const { data } = await jobAdAPI.updateJobAd(activeOrg.id, activeProfile.id, adModal.ad.id, formData);
      setAds((prev) => prev.map((a) => a.id === adModal.ad.id ? data.data.ad : a));
    } else {
      const { data } = await jobAdAPI.createJobAd(activeOrg.id, activeProfile.id, formData);
      setAds((prev) => [data.data.ad, ...prev]);
    }
    setAdModal(null);
  };

  if (loading) return <div style={{ padding: 32, color: 'var(--text-muted)' }}>Loading…</div>;

  return (
    <div>
      {adModal && (
        <JobAdModal
          ad={adModal.ad}
          contacts={contacts}
          onSave={handleSaveAd}
          onCancel={() => setAdModal(null)}
        />
      )}

      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Job Ads</h1>
        {isAdmin && (
          <AddBtn onClick={() => setAdModal({ mode: 'create' })} size={14} title="Create Job Ad" />
        )}
      </div>

      {ads.length === 0 ? (
        <div className="section-box">
          <div className="section-box-body" style={{ textAlign: 'center', padding: '48px 20px' }}>
            <FileText size={40} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
            <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>No job ads created yet.</p>
            {isAdmin && (
              <button className="btn btn-primary btn-sm" onClick={() => setAdModal({ mode: 'create' })}>
                Create First Job Ad
              </button>
            )}
          </div>
        </div>
      ) : (
        ads.map((ad) => (
          <JobAdCard
            key={ad.id}
            ad={ad}
            contacts={contacts}
            isAdmin={isAdmin}
            onUpdated={(updated) => setAds((prev) => prev.map((a) => a.id === updated.id ? updated : a))}
            onDeleted={(id) => setAds((prev) => prev.filter((a) => a.id !== id))}
            onEdit={(ad) => setAdModal({ mode: 'edit', ad })}
            onSelect={(id) => setLastAdId(id)}
          />
        ))
      )}
    </div>
  );
};
